import type { TBotConfig } from '~entities/bot';
import { gridApi } from '~services/api/grid';
import { api, apiTags } from '~shared/api/origin';

import { getCreditsApi } from '../credits/dynamic';
import type {
	GetBotAccessInfoApiResponse,
	GetBotsAvailabilityApiResponse,
	GetContactsApiResponse,
	GetContactsApiResponseTransformed,
} from './types';
import { transformChatsMessage } from './utils';

export const {
	endpoints,
	util,
	useGetContactsQuery,
	useGetBotsAvailabilityQuery,
	useReportAndDeleteBotMutation,
	useChangeBotAccessMutation,
	useGetBotAccessInfoQuery,
	useGetBotProfileQuery,
} = api.injectEndpoints({
	endpoints: (build) => ({
		getContacts: build.query<
			GetContactsApiResponseTransformed,
			{ userId: string }
		>({
			async queryFn({ userId }, _queryApi, _extraOptions, fetchWithBQ) {
				const [botsResponse, availabilityResponse] = await Promise.all([
					fetchWithBQ(`/ai/configuration/${userId}/bots`),
					fetchWithBQ(`/ai/chats/userId/${userId}/v2`),
				]);
				const error = botsResponse.error || availabilityResponse.error;
				if (error) {
					return { error };
				}

				const bots = botsResponse.data as GetContactsApiResponse;
				const availability = (
					availabilityResponse.data as GetBotsAvailabilityApiResponse
				).chats;

				const botsTransformed: GetContactsApiResponseTransformed = {
					contacts: {},
					inaccessibleContacts: {},
					nsfwCount: (
						availabilityResponse.data as GetBotsAvailabilityApiResponse
					).nsfwCount,
				};
				for (const key of Object.keys(bots)) {
					const botId = bots[key].clientId;
					const botAvailableInfo = availability.find(({ uid }) => key === uid);
					if (botAvailableInfo) {
						botsTransformed.contacts[botId] = {
							...bots[key],
							lastMessage: botAvailableInfo.lastMessage
								? transformChatsMessage(botAvailableInfo.lastMessage)
								: null,
							unreadMessagesCount: botAvailableInfo.counters.unreadCount,
							totalMessagesCount: botAvailableInfo.counters.totalCount,
							preConditions: botAvailableInfo.conditions?.preCondition,
							postConditions: botAvailableInfo.conditions?.postCondition,
							statistics: botAvailableInfo.statistics,
							generations: [],
							isTyping: false,
						};
					} else {
						botsTransformed.inaccessibleContacts[botId] = {
							...bots[key],
						};
					}
				}

				return {
					data: botsTransformed,
				};
			},
			async onQueryStarted(_, { dispatch }) {
				const creditsApi = await getCreditsApi();
				dispatch(creditsApi.util.invalidateTags([apiTags.shop]));
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.userId}`;
			},
			providesTags: [apiTags.contacts],
		}),

		getBotsAvailability: build.query<GetBotsAvailabilityApiResponse, string>({
			async queryFn(userId, _queryApi, _extraOptions, fetchWithBQ) {
				const response = await fetchWithBQ(`/ai/chats/userId/${userId}/v2`);
				return response.error
					? {
							error: response.error,
					  }
					: {
							data: response.data as GetBotsAvailabilityApiResponse,
					  };
			},
			async onQueryStarted(userId, { dispatch, queryFulfilled }) {
				try {
					const { data: res } = await queryFulfilled;
					dispatch(
						util.updateQueryData('getContacts', { userId }, (draft) => {
							for (const key of Object.keys(draft.contacts)) {
								const botAvailableInfo = res.chats.find(
									({ botId }) => key === botId
								);
								if (botAvailableInfo) {
									draft.contacts[key].preConditions =
										botAvailableInfo.conditions?.preCondition;
									draft.contacts[key].postConditions =
										botAvailableInfo.conditions?.postCondition;
								}
							}
						})
					);
				} catch {}
			},
		}),
		reportAndDeleteBot: build.mutation<
			{},
			{ userId: string; recipientId: string; reason: string }
		>({
			query: ({ userId, recipientId, reason }) => ({
				url: `/ai/bots/${userId}/${recipientId}/reports`,
				method: 'POST',
				body: {
					reason,
				},
			}),
			async onQueryStarted(
				{ userId, recipientId },
				{ dispatch, queryFulfilled, getState }
			) {
				try {
					await queryFulfilled;
					dispatch(
						util.updateQueryData('getContacts', { userId }, (draft) => {
							delete draft.contacts[recipientId];
						})
					);

					for (const { originalArgs } of gridApi.util.selectInvalidatedBy(
						getState(),
						[apiTags.grid]
					)) {
						dispatch(
							gridApi.util.updateQueryData('getGrid', originalArgs, (draft) => {
								draft.botGrid = draft.botGrid.filter(
									({ clientId }) => clientId !== +recipientId
								);
							})
						);
					}
				} catch {}
			},
		}),
		changeBotAccess: build.mutation<
			{},
			{ userId: string; recipientId: string; access: 'public' | 'private' }
		>({
			query: ({ userId, recipientId, access }) => ({
				url: `/ai/bots/${userId}/${recipientId}/profile`,
				method: 'PATCH',
				body: {
					access,
				},
			}),
			onQueryStarted({ userId, recipientId, access }, { dispatch }) {
				dispatch(
					util.updateQueryData('getContacts', { userId }, (draft) => {
						draft.contacts[recipientId].access = access;
					})
				);
			},
			invalidatesTags: [apiTags.grid],
		}),

		getBotProfile: build.query<
			TBotConfig | null,
			{ userId: string; recipientId: string }
		>({
			async queryFn(
				{ userId, recipientId },
				_queryApi,
				_extraOptions,
				fetchWithBQ
			) {
				const response = await fetchWithBQ(
					`/ai/bots/${userId}/${recipientId}/profile`
				);

				if (!response.error) {
					return { data: response.data as TBotConfig };
				} else if (
					response.error.status === 403 ||
					response.error.status === 404
				) {
					return { data: null };
				}
				return {
					error: response.error,
				};
			},
		}),

		getBotAccessInfo: build.query<
			GetBotAccessInfoApiResponse,
			{ userId: string; recipientId: string }
		>({
			query: ({ userId, recipientId }) => ({
				url: `/ai/bots/${userId}/${recipientId}/access`,
			}),
		}),
	}),
});
